import * as React from "react";
import { MemberPicker, type MemberOption } from "@aisquare/ui";
import { Section, VariantBlock } from "../lib/Section";

// A small inline member list standing in for a workspace's roster. The
// picker is data-agnostic: it never fetches, the caller passes `members`.
const MEMBERS: MemberOption[] = [
  { id: 1, name: "Ada Lovelace", email: "ada@aisquare.studio", username: "ada", role: "owner", avatar: "https://i.pravatar.cc/80?img=5" },
  { id: 2, name: "Grace Hopper", email: "grace@aisquare.studio", username: "grace", role: "admin" },
  { id: 3, name: "Alan Turing", email: "alan@aisquare.studio", username: "alan", role: "editor" },
  { id: 4, name: "Katherine Johnson", email: "katherine@aisquare.studio", username: "kjohnson", role: "editor" },
  { id: 5, name: "Margaret Hamilton", email: "margaret@aisquare.studio", username: "mham", role: "viewer" },
];

export function MemberPickerDemo() {
  // Default picker: selection lives in state, click the trigger to open the
  // cmdk search, type to filter across name / handle / email / role.
  const [picked, setPicked] = React.useState<MemberOption[]>([]);

  // Populated: starts with two co-creators already chosen (AvatarGroup + chips).
  const [preselected, setPreselected] = React.useState<MemberOption[]>([MEMBERS[0], MEMBERS[2]]);

  // Lifecycle demo: flip between ok / loading / error to show the four list
  // states the picker owns (skeletons, retry, empty / no-match copy).
  const [lifecycle, setLifecycle] = React.useState<MemberOption[]>([]);
  const [state, setState] = React.useState<"ok" | "loading" | "error">("loading");

  // Compact density + role badges hidden, for dense surfaces.
  const [compact, setCompact] = React.useState<MemberOption[]>([MEMBERS[1]]);

  return (
    <Section
      id="member-picker"
      title="MemberPicker"
      description="Searchable, keyboard-accessible multi-select for workspace members (co-creators / share targets). Composes cmdk inside the Popover primitive, with selections surfaced as an AvatarGroup plus removable chips; the a11y (listbox/option roles, full keyboard nav) the donor pickers hand-rolled now comes for free."
    >
      <VariantBlock label="Default" caption="open the trigger, type to filter, click a row to add">
        <div className="w-full max-w-md">
          <MemberPicker members={MEMBERS} value={picked} onChange={setPicked} />
        </div>
      </VariantBlock>

      <VariantBlock label="Populated" caption="starts with two members selected (AvatarGroup + chips)">
        <div className="w-full max-w-md">
          <MemberPicker members={MEMBERS} value={preselected} onChange={setPreselected} />
        </div>
      </VariantBlock>

      <VariantBlock label="List states" caption="loading skeletons, an error + Retry, then the live list">
        <div className="w-full max-w-md space-y-3">
          <div className="flex flex-wrap gap-1.5">
            {(["ok", "loading", "error"] as const).map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setState(s)}
                className={
                  "rounded-full border px-2.5 py-1 text-xs font-medium transition-colors " +
                  (state === s
                    ? "border-primary/30 bg-primary/10 text-primary"
                    : "border-border text-muted-foreground hover:text-foreground")
                }
              >
                {s}
              </button>
            ))}
          </div>
          <MemberPicker
            members={MEMBERS}
            value={lifecycle}
            onChange={setLifecycle}
            state={state}
            onRetry={() => setState("loading")}
          />
        </div>
      </VariantBlock>

      <VariantBlock label="Compact + limited access" caption="dense rows with no role badge, beside the read-only lock panel">
        <div className="grid w-full max-w-md gap-4">
          <MemberPicker
            members={MEMBERS}
            value={compact}
            onChange={setCompact}
            density="compact"
            size="sm"
            hideRoleBadge
          />
          <MemberPicker members={MEMBERS} value={[]} onChange={() => {}} canManage={false} />
        </div>
      </VariantBlock>
    </Section>
  );
}
